import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { formatDateTime, rupiah, num } from "@/lib/format";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Activity, CheckCircle2, AlertTriangle, XCircle, Play, Wrench, Link2, Flag, Sparkles, Recycle, Eraser, Trash2, History, Undo2 } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = ["Stock Consistency", "Transfer", "Transactions", "Production", "Opening Balance", "Negative Stock", "Orphan Movement", "Duplicate", "Field Validation"];

const ACTION_META = {
  relink: { label: "Tautkan ulang", cls: "bg-emerald-100 text-emerald-800", icon: <Link2 className="h-3 w-3 mr-1" /> },
  mark_review: { label: "Tandai Review", cls: "bg-amber-100 text-amber-800", icon: <Flag className="h-3 w-3 mr-1" /> },
  delete: { label: "Hapus movement (backup)", cls: "bg-rose-100 text-rose-800", icon: <Trash2 className="h-3 w-3 mr-1" /> },
  generate: { label: "Generate movement hilang", cls: "bg-blue-100 text-blue-800", icon: <Sparkles className="h-3 w-3 mr-1" /> },
  rebuild: { label: "Rebuild movement", cls: "bg-blue-100 text-blue-800", icon: <Recycle className="h-3 w-3 mr-1" /> },
  purge_residual: { label: "Hapus residual", cls: "bg-rose-100 text-rose-800", icon: <Eraser className="h-3 w-3 mr-1" /> },
};

export default function SystemHealth() {
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [repairOpen, setRepairOpen] = useState(false);
  const [plans, setPlans] = useState(null);
  const [repairing, setRepairing] = useState(false);
  const [overrides, setOverrides] = useState({});
  const [repairLogs, setRepairLogs] = useState([]);

  const loadLatest = useCallback(async () => {
    try {
      const [h, rl] = await Promise.all([api.get("/system-health/latest"), api.get("/repair-logs")]);
      if (!h.data.empty) setResult(h.data);
      setRepairLogs(rl.data || []);
    } catch (e) { console.error("Gagal memuat health check:", e); }
  }, []);
  useEffect(() => { loadLatest(); }, [loadLatest]);

  const run = async () => {
    setRunning(true);
    try {
      const r = await api.post("/system-health/run");
      setResult(r.data);
      if (r.data.error_count === 0 && r.data.warning_count === 0) toast.success("Pemeriksaan selesai — tidak ada masalah");
      else toast.warning(`Pemeriksaan selesai — ${r.data.error_count} error, ${r.data.warning_count} warning ditemukan`);
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal menjalankan pemeriksaan"); }
    finally { setRunning(false); }
  };

  const openRepair = async () => {
    setRepairOpen(true);
    setPlans(null);
    try {
      const r = await api.post("/system-health/repair/plan");
      setPlans(r.data.plans);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal menyusun rencana perbaikan");
      setRepairOpen(false);
    }
  };

  const execRepair = async () => {
    if (!window.confirm("Perbaikan akan mengubah relasi data histori stok. Data asli tidak akan dihapus tanpa backup. Lanjutkan?")) return;
    setRepairing(true);
    try {
      const r = await api.post("/system-health/repair/execute", { overrides });
      const parts = Object.entries(r.data).filter(([k, v]) => typeof v === "number" && !["repaired"].includes(k) && v > 0).map(([k, v]) => `${v} ${k}`).join(", ");
      toast.success(`Perbaikan selesai — ${r.data.repaired} item diproses${parts ? ` (${parts})` : ""}. ID: ${r.data.repair_id}`);
      setRepairOpen(false);
      await run(); // RECHECK otomatis setelah repair
    } catch (e) {
      toast.error(e.response?.data?.detail || "Repair gagal — seluruh perubahan di-rollback");
    } finally { setRepairing(false); }
  };

  const issues = [...(result?.errors || []).map(e => ({ ...e, level: "error" })), ...(result?.warnings || []).map(w => ({ ...w, level: "warning" }))];
  const byCat = (cat) => issues.filter(i => i.category === cat);

  const restoreRepair = async (rid) => {
    if (!window.confirm("Pulihkan perbaikan ini? Movement yang dihapus/diubah akan dikembalikan persis seperti semula (stok mengikuti ledger).")) return;
    try {
      const r = await api.post(`/repair-logs/${rid}/restore`);
      toast.success(`Dipulihkan — ${r.data.restored_movements} movement dikembalikan`);
      await run(); // re-check otomatis
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal memulihkan"); }
  };

  return (
    <div data-testid="page-system-health" className="space-y-6">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-2">Kontrol Kualitas Data</div>
          <h1 className="font-display text-3xl font-bold text-slate-900">Pemeriksaan Sistem</h1>
          <p className="text-slate-500 text-sm mt-1">Deteksi → Identifikasi → Review → Repair → Audit → Recheck. Perbaikan tidak menghapus data & tidak mengubah angka stok — setiap repair tercatat di audit trail.</p>
        </div>
        <div className="flex gap-2">
          {result?.error_count > 0 && (
            <Button variant="outline" onClick={openRepair} className="border-rose-300 text-rose-700 hover:bg-rose-50" data-testid="btn-repair-data">
              <Wrench className="h-4 w-4 mr-1" />Perbaiki Data Bermasalah
            </Button>
          )}
          <Button onClick={run} disabled={running} className="bg-slate-900 hover:bg-slate-800" data-testid="btn-run-health">
            <Play className="h-4 w-4 mr-1" />{running ? "Memeriksa..." : "Jalankan Pemeriksaan Sekarang"}
          </Button>
        </div>
      </div>

      {!result && (
        <Card className="p-10 text-center text-slate-400">
          <Activity className="h-10 w-10 mx-auto mb-3 text-slate-300" />
          Belum ada hasil pemeriksaan. Klik "Jalankan Pemeriksaan Sekarang".
        </Card>
      )}

      {result && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="health-summary">
            <div className="bg-white rounded-lg border p-5">
              <div className="text-xs font-mono uppercase tracking-wider text-slate-500">Record Diperiksa</div>
              <div className="font-mono font-bold text-2xl">{result.records_checked}</div>
              <div className="text-xs text-slate-500 mt-1">{result.info?.movements} movement · {result.info?.products} produk · {result.info?.daily_days_checked || 0} hari</div>
            </div>
            <SumBox icon={<CheckCircle2 />} label="Passed" value={result.passed} cls="bg-emerald-500 text-white" testid="health-passed" />
            <SumBox icon={<AlertTriangle />} label="Warning" value={result.warning_count} cls="bg-amber-500 text-white" testid="health-warnings" />
            <SumBox icon={<XCircle />} label="Error" value={result.error_count} cls="bg-rose-500 text-white" testid="health-errors" />
          </div>
          <div className="text-xs text-slate-500 font-mono">Terakhir dijalankan: {formatDateTime(result.run_at)} oleh {result.user_email}</div>

          {repairLogs.length > 0 && (
            <Card data-testid="repair-history">
              <CardHeader className="py-3 flex-row items-center gap-2 space-y-0">
                <History className="h-4 w-4 text-slate-500" />
                <CardTitle className="text-sm">Riwayat Perbaikan</CardTitle>
              </CardHeader>
              <CardContent className="pt-0 space-y-1.5">
                {repairLogs.slice(0, 5).map((l) => (
                  <div key={l.repair_id} className="flex items-center gap-3 rounded border p-2.5 text-xs" data-testid={`repair-log-${l.repair_id}`}>
                    <span className="font-mono">{l.repair_id}</span>
                    <span className="text-slate-500">{formatDateTime(l.created_at)}</span>
                    <span className="text-slate-500">{l.user_email}</span>
                    <span>{(l.items || []).length} item · {l.status === "rolled_back" ? "di-rollback" : "selesai"}</span>
                    {l.restored_at
                      ? <Badge className="bg-slate-100 text-slate-600 font-normal">dipulihkan {formatDateTime(l.restored_at)}</Badge>
                      : l.status === "done" && (
                        <Button variant="outline" size="sm" className="h-6 text-xs ml-auto" onClick={() => restoreRepair(l.repair_id)} data-testid={`restore-${l.repair_id}`}>
                          <Undo2 className="h-3 w-3 mr-1" />Pulihkan
                        </Button>
                      )}
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
            {CATEGORIES.map((cat) => {
              const list = byCat(cat);
              const errs = list.filter(i => i.level === "error").length;
              return (
                <Card key={cat} data-testid={`cat-${cat.replace(/\s/g, "-")}`}>
                  <CardHeader className="py-3 flex-row items-center justify-between space-y-0">
                    <CardTitle className="text-sm">{cat}</CardTitle>
                    {list.length === 0
                      ? <Badge className="bg-emerald-100 text-emerald-800 font-normal">OK</Badge>
                      : <Badge className={errs ? "bg-rose-100 text-rose-800 font-normal" : "bg-amber-100 text-amber-800 font-normal"}>{errs ? `${errs} error` : `${list.length} warning`}</Badge>}
                  </CardHeader>
                  {list.length > 0 && (
                    <CardContent className="pt-0 space-y-1.5 max-h-72 overflow-y-auto">
                      {list.map((i, idx) => (
                        <button key={`${cat}-${idx}`} onClick={() => setExpanded(expanded === `${cat}-${idx}` ? null : `${cat}-${idx}`)}
                          data-testid={`issue-${cat.replace(/\s/g, "-")}-${idx}`}
                          className={`w-full text-left rounded border-l-4 p-2.5 text-xs ${i.level === "error" ? "bg-rose-50 border-rose-500" : "bg-amber-50 border-amber-500"}`}>
                          <div className="font-semibold">{i.title}</div>
                          {expanded === `${cat}-${idx}` && (
                            <div className="mt-1 font-mono text-[11px] leading-relaxed">
                              <div>{i.detail}</div>
                              {i.meta && (
                                <div className="mt-2 grid grid-cols-[110px_1fr] gap-x-3 gap-y-1 bg-white/70 rounded p-2 border border-slate-200">
                                  <span className="text-slate-500">Movement ID</span><span className="break-all">{(i.meta.movement_ids || []).join(", ")}</span>
                                  <span className="text-slate-500">Tanggal</span><span>{i.meta.transaction_date}</span>
                                  <span className="text-slate-500">SKU · Barang</span><span>{i.meta.sku} · {i.meta.product_name}</span>
                                  <span className="text-slate-500">Jumlah</span><span>{i.meta.qty}</span>
                                  <span className="text-slate-500">Lokasi</span><span>{i.meta.location}</span>
                                  <span className="text-slate-500">Movement Type</span><span>{i.meta.type}</span>
                                  <span className="text-slate-500">Reference</span><span className="break-all">{i.meta.ref_type} / {i.meta.ref_id}</span>
                                  <span className="text-slate-500">Nilai</span><span>{rupiah(i.meta.value)}</span>
                                  <span className="text-slate-500">Dokumen Induk</span><span>{i.meta.ref_no} (tidak ditemukan)</span>
                                  <span className="text-slate-500">Penyebab</span><span className="font-sans">{i.meta.cause}</span>
                                  <span className="text-slate-500">Rekomendasi</span><span className="font-sans">{i.meta.recommendation}</span>
                                </div>
                              )}
                            </div>
                          )}
                        </button>
                      ))}
                    </CardContent>
                  )}
                </Card>
              );
            })}
          </div>
        </>
      )}

      <Dialog open={repairOpen} onOpenChange={setRepairOpen}>
        <DialogContent className="max-w-2xl" data-testid="repair-dialog">
          <DialogHeader>
            <DialogTitle>Perbaiki Data Bermasalah</DialogTitle>
            <DialogDescription>Identifikasi & rencana perbaikan orphan stock movement. Tidak ada data yang dihapus; setiap perubahan dicatat di repair log & audit trail.</DialogDescription>
          </DialogHeader>
          {plans === null && <div className="py-8 text-center text-slate-400 text-sm">Menganalisis orphan movement...</div>}
          {plans !== null && plans.length === 0 && <div className="py-8 text-center text-slate-400 text-sm" data-testid="repair-empty">Tidak ada orphan movement yang perlu diperbaiki.</div>}
          {plans !== null && plans.length > 0 && (
            <>
              <div className="text-sm font-semibold" data-testid="repair-count">{plans.length} temuan perlu diperbaiki.</div>
              <div className="max-h-72 overflow-y-auto space-y-2">
                {plans.map((p, i) => {
                  const effAction = p.kind === "orphan" && p.action !== "relink"
                    ? (overrides[p.movement_id] === "review" ? "mark_review" : "delete")
                    : p.action;
                  const meta = ACTION_META[effAction] || ACTION_META.mark_review;
                  return (
                    <div key={p.movement_id || `${p.ref_id}-${p.product_id}-${p.primary || "x"}`} className="rounded border p-3 text-xs" data-testid={`repair-plan-${i}`}>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono font-bold">{p.ref_no || "(tanpa no ref)"}</span>
                        <span className="font-mono">{p.sku} · {p.product_name}</span>
                        {p.kind === "orphan"
                          ? <span className="font-mono">{p.type} {num(p.qty)}</span>
                          : <span className="font-mono">{p.label} — movement {num(p.actual)} → seharusnya {num(p.expected)}</span>}
                        <Badge className={`${meta.cls} font-normal`}>{meta.icon}{meta.label}</Badge>
                      </div>
                      <div className="text-slate-500 mt-1">{p.transaction_date} · {p.ref_type}/{String(p.ref_id).slice(0, 18)}…{p.value != null ? ` · ${rupiah(p.value)}` : ""}</div>
                      <div className="mt-1 text-slate-600">{p.reason}</div>
                      {p.parent_info && <div className="mt-1 text-emerald-700">→ Dokumen ditemukan: {p.parent_info.trx_no}{p.parent_info.is_deleted ? " (soft-deleted)" : ""}</div>}
                      {p.kind === "orphan" && p.action !== "relink" && (
                        <div className="mt-2 flex items-center gap-2">
                          <span className="text-slate-500">Tindakan:</span>
                          <Select value={overrides[p.movement_id] || "delete"} onValueChange={(v) => setOverrides({ ...overrides, [p.movement_id]: v })}>
                            <SelectTrigger className="h-7 w-56 text-xs" data-testid={`repair-action-${i}`}><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="delete">Hapus Movement (backup di repair log)</SelectItem>
                              <SelectItem value="review">Tandai Review (stok tak berubah)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="rounded bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800">
                Perbaikan akan mengubah relasi data histori stok. Semua data asli di-backup ke repair log sebelum diubah — jika satu perbaikan gagal, seluruh perubahan di-rollback. Generate/rebuild movement menyesuaikan stok mengikuti dokumen transaksi (stok tetap dihitung dari ledger).
              </div>
            </>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRepairOpen(false)}>Batal</Button>
            {plans !== null && plans.length > 0 && (
              <Button onClick={execRepair} disabled={repairing} className="bg-rose-600 hover:bg-rose-700" data-testid="btn-execute-repair">
                <Wrench className="h-4 w-4 mr-1" />{repairing ? "Memperbaiki..." : "Periksa & Perbaiki"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SumBox({ icon, label, value, cls, testid }) {
  return (
    <div data-testid={testid} className="bg-white rounded-lg border p-5">
      <div className={`h-10 w-10 rounded grid place-items-center mb-3 ${cls}`}>{React.cloneElement(icon, { className: "h-5 w-5" })}</div>
      <div className="text-xs font-mono uppercase tracking-wider text-slate-500">{label}</div>
      <div className="font-mono font-bold text-2xl">{value}</div>
    </div>
  );
}
